class PriceCalculator extends HTMLElement {
  connectedCallback() {
    this.sheetIndex = this.dataset.sheetIndex;
    this.shape = this.dataset.shape || '';
    this.qtyModel = this.dataset.qtyModel;
    this.isCustomOnly = this.dataset.isCustomOnly === 'true';
    this.hasShapeToggle = this.dataset.hasShapeToggle === 'true';
    this.hideLaminate = this.dataset.hideLaminate === 'true';
    this.isPuzzle = this.dataset.isPuzzle === 'true';
    this.isCoaster = this.sheetIndex === '5';

    this.selectedWidth = null;
    this.selectedHeight = null;
    this.selectedQty = null;
    this.selectedLaminate = 'Gloss';
    this.selectedShape = this.shape || 'Circle';
    this.selectedMaterial = 'BrownKraft';
    this.selectedPuzzlePieces = null;
    this.selectedPuzzlePrice = null;

    this.qtyListEl = this.querySelector('.ll-qty-list');
    this.priceDisplayEl = this.querySelector('.ll-price-display');
    this.checkoutBtn = this.querySelector('.ll-checkout-btn');

    this.bindSizeButtons();
    this.bindCustomSizeInputs();
    this.bindShapeButtons();
    this.bindLaminateButtons();
    this.bindMaterialButtons();
    this.bindPuzzleButtons();
    this.bindCheckoutButton();

    if (this.isPuzzle) {
      const firstPuzzle = this.querySelector('.ll-puzzle-size-btn');
      if (firstPuzzle) {
        this.selectedPuzzlePieces = firstPuzzle.dataset.pieces;
        this.selectedPuzzlePrice = parseFloat(firstPuzzle.dataset.price);
      }
      this.renderPuzzleQuantities();
    } else if (!this.isCustomOnly) {
      // Check for ?variant= in URL and activate matching size button
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');

      if (variantId) {
        fetch(window.location.pathname + '.json')
          .then(r => r.json())
          .then(data => {
            const variant = data.product.variants.find(v => String(v.id) === String(variantId));
            if (variant) {
              const sizeStr = variant.option1.replace(/"/g, '').trim();
              const parts = sizeStr.split('x').map(s => s.trim());
              const w = parseFloat(parts[0]);
              const h = parseFloat(parts[1]);
              if (!isNaN(w) && !isNaN(h)) {
                const sizeButtons = this.querySelectorAll('.ll-size-btn:not(.ll-custom-btn)');
                sizeButtons.forEach(b => b.classList.remove('active'));
                const matchBtn = Array.from(sizeButtons).find(b =>
                  parseFloat(b.dataset.w) === w && parseFloat(b.dataset.h) === h
                );
                if (matchBtn) {
                  matchBtn.classList.add('active');
                  this.selectedWidth = w;
                  this.selectedHeight = h;
                } else {
                  const customBtn = this.querySelector('.ll-custom-btn');
                  if (customBtn) {
                    customBtn.classList.add('active');
                    const customRow = this.querySelector('.ll-custom-size-row');
                    if (customRow) customRow.style.display = 'flex';
                    const wInput = this.querySelector('.ll-custom-width');
                    const hInput = this.querySelector('.ll-custom-height');
                    if (wInput) wInput.value = w;
                    if (hInput) hInput.value = h;
                    this.selectedWidth = w;
                    this.selectedHeight = h;
                  }
                }
                this.renderQuantities();
              }
            } else {
              const firstSize = this.querySelector('.ll-size-btn:not(.ll-custom-btn)');
              if (firstSize) { this.selectedWidth = parseFloat(firstSize.dataset.w); this.selectedHeight = parseFloat(firstSize.dataset.h); }
              this.renderQuantities();
            }
          })
          .catch(() => {
            const firstSize = this.querySelector('.ll-size-btn:not(.ll-custom-btn)');
            if (firstSize) { this.selectedWidth = parseFloat(firstSize.dataset.w); this.selectedHeight = parseFloat(firstSize.dataset.h); }
            this.renderQuantities();
          });
      } else {
        const firstSize = this.querySelector('.ll-size-btn:not(.ll-custom-btn)');
        if (firstSize) { this.selectedWidth = parseFloat(firstSize.dataset.w); this.selectedHeight = parseFloat(firstSize.dataset.h); }
        this.renderQuantities();
      }
    }
  }

  // ── Qty tiers per sheet ───────────────────────────────────────────────────
  getQtyTiers() {
    switch (this.sheetIndex) {
      case '4': return [2, 4, 6, 8, 10, 20, 50, 200, 300, 500, 1000, 2000, 3000, 5000, 10000];
      case '5': return [50, 100, 200, 250, 500, 1000, 2000, 3000, 4000, 5000];
      case '2': return [50, 100, 200, 300, 500, 1000, 2000, 3000, 5000];
      case '3': return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
      default:  return [50, 100, 200, 300, 500, 1000, 2000, 3000, 5000, 10000];
    }
  }

  // ── Price lookup ──────────────────────────────────────────────────────────
  lookupPriceMap(w, h) {
    const sheetData = window.labelLlamaPrices && window.labelLlamaPrices[this.sheetIndex];
    if (!sheetData) return null;

    if (this.isCoaster) {
      const matKey = this.selectedMaterial;
      const k1 = `${matKey}:${this.fmtKey(w)}x${this.fmtKey(h)}`;
      const k2 = `${matKey}:${this.fmtKey(h)}x${this.fmtKey(w)}`;
      return sheetData[k1] || sheetData[k2] || null;
    }

    const exactKey1 = `${this.fmtKey(w)}x${this.fmtKey(h)}`;
    const exactKey2 = `${this.fmtKey(h)}x${this.fmtKey(w)}`;
    if (sheetData[exactKey1]) return sheetData[exactKey1];
    if (sheetData[exactKey2]) return sheetData[exactKey2];

    const rw = Math.round(w);
    const rh = Math.round(h);
    const roundKey1 = `${rw}x${rh}`;
    const roundKey2 = `${rh}x${rw}`;
    return sheetData[roundKey1] || sheetData[roundKey2] || null;
  }

  fmtKey(n) {
    return n === Math.floor(n) ? String(Math.floor(n)) : String(n);
  }

  // ── Render quantities ─────────────────────────────────────────────────────
  renderQuantities() {
    if (this.selectedWidth == null || this.selectedHeight == null) return;

    const tiers = this.getQtyTiers();
    const priceMap = this.lookupPriceMap(this.selectedWidth, this.selectedHeight);

    this.qtyListEl.innerHTML = '';

    if (!priceMap) {
      this.qtyListEl.innerHTML = '<div class="ll-no-data">Pricing not available for this size yet.</div>';
      this.priceDisplayEl.textContent = '-';
      if (this.checkoutBtn) this.checkoutBtn.disabled = true;
      return;
    }

    let firstPrice = null;

    tiers.forEach((qty, idx) => {
      const price = priceMap[qty];
      if (price == null) return;

      if (firstPrice === null) firstPrice = parseFloat(price);

      const row = document.createElement('div');
      row.className = 'll-qty-row';
      if (idx === 0) row.classList.add('active');
      row.dataset.qty = qty;
      row.dataset.price = parseFloat(price).toFixed(2);

      let saveLabel = '';
      if (idx > 0 && firstPrice > 0) {
        const expectedNoDiscount = firstPrice * qty / tiers[0];
        const savePct = Math.round(100 * (expectedNoDiscount - price) / expectedNoDiscount);
        if (savePct > 0) saveLabel = `Save ${savePct}%`;
      }

      row.innerHTML = `
        <span class="ll-qty-units">${qty}</span>
        <span class="ll-qty-price">$${parseFloat(price).toFixed(2)}</span>
        <span class="ll-qty-save">${saveLabel}</span>
      `;

      row.addEventListener('click', () => {
        this.qtyListEl.querySelectorAll('.ll-qty-row').forEach(r => r.classList.remove('active'));
        row.classList.add('active');
        this.selectedQty = qty;
        this.updatePriceDisplay();
      });

      this.qtyListEl.appendChild(row);
      if (idx === 0) this.selectedQty = qty;
    });

    this.updatePriceDisplay();
  }

  renderPuzzleQuantities() {
    const tiers = [1, 2, 3, 4, 5];
    this.qtyListEl.innerHTML = '';

    tiers.forEach((qty, idx) => {
      const totalPrice = this.selectedPuzzlePrice * qty;
      const row = document.createElement('div');
      row.className = 'll-qty-row';
      if (idx === 0) row.classList.add('active');
      row.dataset.qty = qty;
      row.dataset.price = totalPrice.toFixed(2);

      row.innerHTML = `
        <span class="ll-qty-units">${qty}</span>
        <span class="ll-qty-price">$${totalPrice.toFixed(2)}</span>
        <span class="ll-qty-save"></span>
      `;

      row.addEventListener('click', () => {
        this.qtyListEl.querySelectorAll('.ll-qty-row').forEach(r => r.classList.remove('active'));
        row.classList.add('active');
        this.selectedQty = qty;
        this.updatePriceDisplay();
      });

      this.qtyListEl.appendChild(row);
      if (idx === 0) this.selectedQty = qty;
    });

    this.updatePriceDisplay();
  }

  // ── Price display ─────────────────────────────────────────────────────────
  updatePriceDisplay() {
    const activeRow = this.qtyListEl.querySelector('.ll-qty-row.active');
    if (!activeRow) {
      this.priceDisplayEl.textContent = '-';
      if (this.checkoutBtn) this.checkoutBtn.disabled = true;
      return;
    }

    const price = parseFloat(activeRow.dataset.price);
    this.priceDisplayEl.textContent = `$${price.toFixed(2)}`;
    if (this.checkoutBtn) this.checkoutBtn.disabled = false;
  }

  // ── Checkout ──────────────────────────────────────────────────────────────
  bindCheckoutButton() {
    if (!this.checkoutBtn) return;

    // Change button text to "Continue" to reflect artwork upload step
    this.checkoutBtn.textContent = 'Continue →';

    this.checkoutBtn.addEventListener('click', async () => {
      const activeRow = this.qtyListEl.querySelector('.ll-qty-row.active');
      if (!activeRow) return;

      const price = activeRow.dataset.price;
      const qty = activeRow.dataset.qty;

      // Build size string
      let sizeStr = '';
      if (this.isPuzzle) {
        sizeStr = `${this.selectedPuzzlePieces}pcs`;
      } else if (this.selectedWidth != null && this.selectedHeight != null) {
        sizeStr = `${this.selectedWidth}" x ${this.selectedHeight}"`;
      }

      const productTitle = document.querySelector('.product__title, h1.title, h1')?.textContent?.trim() || 'Custom Product';

      const orderDetails = {
        productTitle,
        price,
        quantity: qty,
        size: sizeStr,
        laminate: this.hideLaminate ? '' : this.selectedLaminate,
        shape: this.hasShapeToggle ? this.selectedShape : (this.shape || ''),
        material: this.isCoaster ? this.selectedMaterial.replace('BrownKraft', 'Brown Kraft') : '',
        orderId: Date.now().toString(),
      };

      // Open artwork uploader modal
      if (typeof window.llOpenArtworkModal === 'function') {
        window.llOpenArtworkModal(orderDetails, async (artworkData) => {
          // Called after customer approves artwork (or skips)
          this.checkoutBtn.textContent = 'Processing...';
          this.checkoutBtn.disabled = true;

          try {
            // Add artwork URL and instructions to order if provided
            if (artworkData) {
              orderDetails.artworkUrl = artworkData.artworkUrl;
              orderDetails.instructions = artworkData.instructions;
            }

            const response = await fetch('https://labelllama-pricing.vercel.app/api/create-draft-order', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(orderDetails)
            });

            const data = await response.json();

            if (data.checkoutUrl) {
              window.location.href = data.checkoutUrl;
            } else {
              throw new Error(data.error || 'Unknown error');
            }
          } catch (err) {
            alert('Something went wrong. Please try again.');
            this.checkoutBtn.textContent = 'Continue →';
            this.checkoutBtn.disabled = false;
          }
        });
      } else {
        // Fallback if artwork modal not loaded — go straight to checkout
        this.checkoutBtn.textContent = 'Processing...';
        this.checkoutBtn.disabled = true;
        try {
          const response = await fetch('https://labelllama-pricing.vercel.app/api/create-draft-order', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(orderDetails)
          });
          const data = await response.json();
          if (data.checkoutUrl) {
            window.location.href = data.checkoutUrl;
          } else {
            throw new Error(data.error || 'Unknown error');
          }
        } catch (err) {
          alert('Something went wrong. Please try again.');
          this.checkoutBtn.textContent = 'Continue →';
          this.checkoutBtn.disabled = false;
        }
      }
    });
  }

  // ── Event bindings ────────────────────────────────────────────────────────
  bindSizeButtons() {
    const sizeButtons = this.querySelectorAll('.ll-size-btn');
    sizeButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        sizeButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const customRow = this.querySelector('.ll-custom-size-row');

        if (btn.classList.contains('ll-custom-btn')) {
          if (customRow) customRow.style.display = 'flex';
          this.selectedWidth = null;
          this.selectedHeight = null;
          this.qtyListEl.innerHTML = '';
          this.priceDisplayEl.textContent = '-';
          if (this.checkoutBtn) this.checkoutBtn.disabled = true;
        } else {
          if (customRow) customRow.style.display = 'none';
          this.selectedWidth = parseFloat(btn.dataset.w);
          this.selectedHeight = parseFloat(btn.dataset.h);
          this.renderQuantities();
        }
      });
    });
  }

  bindCustomSizeInputs() {
    const widthInput = this.querySelector('.ll-custom-width');
    const heightInput = this.querySelector('.ll-custom-height');
    if (!widthInput || !heightInput) return;

    let debounceTimer;
    const onChange = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const w = parseFloat(widthInput.value);
        const h = parseFloat(heightInput.value);
        if (!w || !h || w <= 0 || h <= 0) return;
        this.clearSizeMessage();
        this.selectedWidth = w;
        this.selectedHeight = h;
        this.renderQuantities();
      }, 600);
    };

    widthInput.addEventListener('input', onChange);
    heightInput.addEventListener('input', onChange);
  }

  bindShapeButtons() {
    const shapeButtons = this.querySelectorAll('.ll-shape-btn');
    shapeButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        shapeButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.selectedShape = btn.dataset.shape;
        this.renderQuantities();
      });
    });
  }

  bindLaminateButtons() {
    const laminateButtons = this.querySelectorAll('.ll-laminate-btn');
    laminateButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        laminateButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.selectedLaminate = btn.dataset.laminate;
        this.updatePriceDisplay();
      });
    });
  }

  bindMaterialButtons() {
    const materialButtons = this.querySelectorAll('.ll-material-btn');
    materialButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        materialButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.selectedMaterial = btn.dataset.material;
        this.renderQuantities();
      });
    });
  }

  bindPuzzleButtons() {
    const puzzleButtons = this.querySelectorAll('.ll-puzzle-size-btn');
    puzzleButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        puzzleButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.selectedPuzzlePieces = btn.dataset.pieces;
        this.selectedPuzzlePrice = parseFloat(btn.dataset.price);
        this.renderPuzzleQuantities();
      });
    });
  }

  showSizeMessage(text, isWarning) {
    let msgEl = this.querySelector('.ll-size-message');
    if (!msgEl) {
      msgEl = document.createElement('div');
      msgEl.className = 'll-size-message';
      const customRow = this.querySelector('.ll-custom-size-row');
      customRow.insertAdjacentElement('afterend', msgEl);
    }
    msgEl.textContent = text;
    msgEl.style.color = isWarning ? '#D87666' : '#0A8FA0';
  }

  clearSizeMessage() {
    const msgEl = this.querySelector('.ll-size-message');
    if (msgEl) msgEl.remove();
  }
}

customElements.define('price-calculator', PriceCalculator);